class BundleUtilities {
  /**
   * Format money amount to currency string
   * @param {number} amount - Amount in cents
   * @param {string} format - Money format string
   * @returns {string} Formatted money string
   */
  static formatMoney(amount, format = '{{amount}} lei') {
    if (typeof amount !== 'number') {
      amount = parseInt(amount, 10);
    }

    const moneyAmount = (amount / 100).toFixed(2);

    return format.replace(/\{\{(\s*amount\s*)\}\}/, moneyAmount);
  }

  /**
   * Debounce function execution
   * @param {Function} func - Function to debounce
   * @param {number} wait - Wait time in milliseconds
   * @param {boolean} immediate - Execute immediately
   * @returns {Function} Debounced function
   */
  static debounce(func, wait, immediate) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        timeout = null;
        if (!immediate) func(...args);
      };
      const callNow = immediate && !timeout;
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
      if (callNow) func(...args);
    };
  }

  /**
   * Show notification message
   * @param {string} message - Message to display
   * @param {string} type - Notification type (success, error, info)
   */
  static showNotification(message, type = 'info') {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `bundle-notification bundle-notification--${type}`;
    notification.innerHTML = `
      <div class="bundle-notification__content">
        <span class="bundle-notification__message">${message}</span>
        <button class="bundle-notification__close" aria-label="Close notification">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M1 1L13 13M1 13L13 1" stroke="currentColor" stroke-width="2"/>
          </svg>
        </button>
      </div>
    `;

    // Add styles if not already present
    if (!document.querySelector('#bundle-notification-styles')) {
      const styles = document.createElement('style');
      styles.id = 'bundle-notification-styles';
      styles.textContent = `
        .bundle-notification {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 10000;
          max-width: 400px;
          padding: 1rem;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          animation: bundle-notification-slide-in 0.3s ease-out;
        }
        .bundle-notification--success {
          background: #d1fae5;
          color: #065f46;
          border: 1px solid #10b981;
        }
        .bundle-notification--error {
          background: #fee2e2;
          color: #991b1b;
          border: 1px solid #ef4444;
        }
        .bundle-notification--info {
          background: #dbeafe;
          color: #1e40af;
          border: 1px solid #3b82f6;
        }
        .bundle-notification__content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
        }
        .bundle-notification__close {
          background: none;
          border: none;
          cursor: pointer;
          padding: 0.25rem;
          opacity: 0.7;
        }
        .bundle-notification__close:hover {
          opacity: 1;
        }
        @keyframes bundle-notification-slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes bundle-notification-slide-out {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(100%);
            opacity: 0;
          }
        }
      `;
      document.head.appendChild(styles);
    }

    // Add to DOM
    document.body.appendChild(notification);

    // Auto-remove after 5 seconds
    const autoRemove = setTimeout(() => {
      this.removeNotification(notification);
    }, 5000);

    // Add close button handler
    notification
      .querySelector('.bundle-notification__close')
      .addEventListener('click', () => {
        clearTimeout(autoRemove);
        this.removeNotification(notification);
      });
  }

  /**
   * Remove notification with animation
   * @param {HTMLElement} notification - Notification element
   */
  static removeNotification(notification) {
    notification.style.animation = 'bundle-notification-slide-out 0.3s ease-in';
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 300);
  }

  /**
   * Validate bundle selections
   * @param {Object} selections - Current bundle selections
   * @param {number} required - Required number of selections
   * @returns {Object} Validation result
   */
  static validateBundleSelections(selections, required) {
    const selectedCount = Object.keys(selections).length;
    const isValid = selectedCount === required;

    return {
      isValid,
      selectedCount,
      required,
      message: isValid
        ? 'Adaugă în coș'
        : `Te rog mai adaugă încă ${required - selectedCount} model${
            required - selectedCount !== 1 ? 'e' : ''
          }`,
    };
  }

  /**
   * Sanitize HTML to prevent XSS
   * @param {string} html - HTML string to sanitize
   * @returns {string} Sanitized HTML
   */
  static sanitizeHTML(html) {
    const temp = document.createElement('div');
    temp.textContent = html;
    return temp.innerHTML;
  }
}

/**
 * Product Bundle Builder
 * Main JavaScript functionality for nail wrap starter bundle selection
 */

class ProductBundleBuilder {
  constructor() {
    this.currentSelectionIndex = null;
    this.selections = {};
    this.filteredVariants = [];
    this.searchTerm = '';

    this.init();
  }

  /**
   * Initialize the bundle builder
   */
  init() {
    // Check if required config is available
    if (!window.bundleConfig) {
      console.error('Bundle configuration not found');
      return;
    }

    this.setupEventListeners();
    this.updateVariants();
    this.updateSubmitButton();
  }

  /**
   * Setup all event listeners
   */
  setupEventListeners() {
    // Selection buttons
    document.querySelectorAll('.bundle-select-button').forEach((button) => {
      button.addEventListener('click', (e) => {
        const index = e.target.dataset.selectionIndex;
        this.openModal(index);
      });
    });

    // Change selection buttons
    document.addEventListener('click', (e) => {
      if (e.target.closest('.bundle-remove-button')) {
        const item = e.target.closest('.bundle-selection-item');
        if (item) {
          const index = item.dataset.selectionIndex;
          this.removeSelection(index);
        }
      }
    });

    // Modal controls
    const modal = document.getElementById('modal-product-variant-selection');
    if (modal) {
      // Close modal
      modal
        .querySelectorAll('.bundle-modal-close, .bundle-cancel-button')
        .forEach((button) => {
          button.addEventListener('click', () => this.closeModal());
        });

      // Overlay click to close
      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          this.closeModal();
        }
      });

      // Product selection
      modal.addEventListener('click', (e) => {
        const productCard = e.target.closest('.bundle-product-card');
        if (productCard) {
          this.selectProduct(productCard);
        }
      });

      // Confirm selection
      modal
        .querySelector('.bundle-confirm-selection')
        ?.addEventListener('click', () => {
          this.confirmSelection();
        });

      // Search input
      const searchInput = modal.querySelector('.bundle-search-input');
      if (searchInput) {
        searchInput.addEventListener(
          'input',
          BundleUtilities.debounce((e) => {
            this.searchTerm = e.target.value.toLowerCase();
            this.updateVariants();
          }, 300)
        );
      }
    }

    // Form submission
    const submitButton = document.querySelector('.product-form__cart-submit');
    if (submitButton) {
      submitButton.addEventListener('click', () => this.handleFormSubmit());
    }

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isModalOpen()) {
        this.closeModal();
      }
    });
  }

  /**
   * Update products display based on search and filters
   */
  updateVariants() {
    const variants = window.bundleConfig.collection.variants;

    // Filter variants
    this.filteredVariants = variants.filter((variant) => {
      // Search filter
      if (
        this.searchTerm &&
        !variant.title.toLowerCase().includes(this.searchTerm)
      ) {
        return false;
      }

      return true;
    });

    this.renderVariants();
  }

  /**
   * Render filtered products in the modal
   */
  renderVariants() {
    const grid = document.getElementById('bundle-products-grid');
    const empty = document.getElementById('bundle-products-empty');
    const template = document.getElementById('bundle-product-card-template');

    if (!grid || !template) return;

    grid.innerHTML = '';

    if (this.filteredVariants.length === 0) {
      grid.style.display = 'none';
      empty.style.display = 'block';
      return;
    }

    grid.style.display = 'grid';
    empty.style.display = 'none';

    this.filteredVariants.forEach((variant) => {
      const card = template.content.cloneNode(true);
      const cardElement = card.querySelector('.bundle-product-card');

      // Set data attributes for variant
      cardElement.dataset.productId = variant.id; // Using variant ID
      cardElement.dataset.productTitle = variant.title;
      cardElement.dataset.productSku = variant.sku || '';
      cardElement.dataset.productStyles = variant.styles?.join(',') || '';
      cardElement.dataset.productColors = variant.colors?.join(',') || '';

      // Set image
      const img = card.querySelector('.bundle-product-image img');
      img.src = (variant.thumb_image || variant.image).replace(/&width=\d+/, '&width=320');
      img.alt = variant.title;

      // Set title
      card.querySelector('.bundle-product-title').textContent = variant.title;

      // Set styles
      const stylesContainer = card.querySelector('.bundle-product-styles');
      if (variant.styles && variant.styles.length > 0) {
        stylesContainer.innerHTML = variant.styles
          .map((style) => `<span>${BundleUtilities.sanitizeHTML(style)}</span>`)
          .join('');
      }

      // Set price
      const priceElement = card.querySelector('.bundle-product-price-amount');
      priceElement.textContent = BundleUtilities.formatMoney(variant.price);

      // Add inventory indicator if low stock
      if (variant.inventory_quantity <= 5 && variant.inventory_quantity > 0) {
        const inventoryWarning = document.createElement('div');
        inventoryWarning.className = 'bundle-product-inventory-warning';
        inventoryWarning.textContent = `Only ${variant.inventory_quantity} left`;
        card
          .querySelector('.bundle-product-info')
          .appendChild(inventoryWarning);
      }

      grid.appendChild(card);
    });
  }

  /**
   * Select a product in the modal
   */
  selectProduct(productCard) {
    // Remove previous selection
    document
      .querySelectorAll('.bundle-product-card.selected')
      .forEach((card) => {
        card.classList.remove('selected');
        card.querySelector('.bundle-product-selected-indicator').style.display =
          'none';
      });

    // Select new product
    productCard.classList.add('selected');
    productCard.querySelector(
      '.bundle-product-selected-indicator'
    ).style.display = 'flex';

    // Enable confirm button
    const confirmButton = document.querySelector('.bundle-confirm-selection');
    if (confirmButton) {
      confirmButton.disabled = false;
    }
  }

  /**
   * Confirm the current selection
   */
  confirmSelection() {
    const selectedCard = document.querySelector(
      '.bundle-product-card.selected'
    );
    if (!selectedCard || this.currentSelectionIndex === null) return;

    const variantId = selectedCard.dataset.productId; // This is now variant ID
    const variant = window.bundleConfig.collection.variants.find(
      (v) => String(v.id) === variantId
    );

    if (variant) {
      this.selections[this.currentSelectionIndex] = variant;
      this.updateSelectionDisplay(this.currentSelectionIndex, variant);
      this.updateSubmitButton();
      this.closeModal();
    }
  }

  /**
   * Update the selection display for a specific slot
   */
  updateSelectionDisplay(index, product) {
    const item = document.querySelector(
      `.bundle-selection-item[data-selection-index="${index}"]`
    );
    if (!item) return;

    const placeholder = item.querySelector('.bundle-selection-placeholder');
    const selected = item.querySelector('.bundle-selection-selected');

    // Hide placeholder, show selected
    placeholder.style.display = 'none';
    selected.style.display = 'flex';

    // Update content
    const img = selected.querySelector('.selected-product-image');
    const title = selected.querySelector('.selected-product-title');
    const styles = selected.querySelector('.selected-product-styles');

    img.src = (product.image || product.thumb_image).replace(/&width=\d+/, '&width=320');
    img.alt = product.title;
    title.textContent = product.title;

    if (product.styles && product.styles.length > 0) {
      styles.textContent = product.styles.join(', ');
    }

    // Mark item as selected
    item.classList.add('selected');
  }

  /**
   * Update the submit button state
   */
  updateSubmitButton() {
    const submitButton = document.querySelector('.product-form__cart-submit');
    const addToCartText = submitButton?.querySelector('.add-to-cart-text');

    if (!submitButton || !addToCartText) return;

    const validation = BundleUtilities.validateBundleSelections(
      this.selections,
      window.bundleConfig.bundle.no_of_required_selections
    );

    if (validation.isValid) {
      submitButton.disabled = false;
      addToCartText.textContent = validation.message;
    } else {
      submitButton.disabled = true;
      addToCartText.textContent = validation.message;
    }
  }

  /**
   * Open the selection modal
   */
  openModal(selectionIndex) {
    this.currentSelectionIndex = selectionIndex;
    const modal = document.getElementById('modal-product-variant-selection');

    if (modal) {
      modal.style.display = 'flex';
      document.body.style.overflow = 'hidden';

      // Reset modal state
      this.clearSelection();
      this.updateVariants();

      // Focus search input
      const searchInput = modal.querySelector('.bundle-search-input');
      if (searchInput) {
        setTimeout(() => searchInput.focus(), 100);
      }
    }
  }

  /**
   * Close the selection modal
   */
  closeModal() {
    const modal = document.getElementById('modal-product-variant-selection');
    if (modal) {
      modal.style.display = 'none';
      document.body.style.overflow = '';
      this.currentSelectionIndex = null;
      this.clearSelection();
    }
  }

  /**
   * Check if modal is open
   */
  isModalOpen() {
    const modal = document.getElementById('modal-product-variant-selection');
    return modal && modal.style.display === 'flex';
  }

  /**
   * Clear current selection in modal
   */
  clearSelection() {
    document
      .querySelectorAll('.bundle-product-card.selected')
      .forEach((card) => {
        card.classList.remove('selected');
        card.querySelector('.bundle-product-selected-indicator').style.display =
          'none';
      });

    const confirmButton = document.querySelector('.bundle-confirm-selection');
    if (confirmButton) {
      confirmButton.disabled = true;
    }
  }

  /**
   * Clear all filters
   */
  clearFilters() {
    this.searchTerm = '';

    const searchInput = document.querySelector('.bundle-search-input');

    if (searchInput) searchInput.value = '';

    this.updateVariants();
  }

  /**
   * Handle form submission
   */
  handleFormSubmit() {
    const validation = BundleUtilities.validateBundleSelections(
      this.selections,
      window.bundleConfig.bundle.no_of_required_selections
    );

    if (!validation.isValid) {
      BundleUtilities.showNotification(validation.message, 'error');
      return;
    }

    this.addBundleToCart();
  }

  /**
   * Add the complete bundle to cart
   */
  async addBundleToCart() {
    const submitButton = document.querySelector('.product-form__cart-submit');
    const realSubmitButton = document.querySelector(
      '.form--bundle button[type="submit"]'
    );

    if (!submitButton) return;

    submitButton.classList.add('loading');
    submitButton.disabled = true;

    try {
      const selectedVariants = Object.values(this.selections);

      if (selectedVariants.length === 0) {
        return;
      }

      const whereToAddItemsContainer = document.querySelector('#custom-inputs');

      const existingInputs = whereToAddItemsContainer.querySelectorAll('input');
      existingInputs.forEach(input => input.remove());
      
      const bundledProductsData = selectedVariants.reduce((acc, variant) => {
        const existing = acc.find(
          (item) => item.variant_id === variant.id.toString()
        );
        if (existing) {
          existing.quantity += 1; // Aggregate quantities
        } else {
          acc.push({
            variant_id: variant.id.toString(),
            handle: variant.handle,
            sku: variant.sku,
            quantity: 1,
          });
        }
        return acc;
      }, []);

      bundledProductsData.forEach((item) => {
        const bundledProductsVariantProperty = document.createElement('input');
        bundledProductsVariantProperty.type = 'hidden';
        bundledProductsVariantProperty.name = `properties[_bundled_products][][variant_id]`;
        bundledProductsVariantProperty.value = item.variant_id;
        whereToAddItemsContainer.appendChild(bundledProductsVariantProperty);

        const bundledProductsHandleProperty = document.createElement('input');
        bundledProductsHandleProperty.type = 'hidden';
        bundledProductsHandleProperty.name = `properties[_bundled_products][][handle]`;
        bundledProductsHandleProperty.value = item.handle;
        whereToAddItemsContainer.appendChild(bundledProductsHandleProperty);

        const bundledProductsQuantityProperty = document.createElement('input');
        bundledProductsQuantityProperty.type = 'hidden';
        bundledProductsQuantityProperty.name = `properties[_bundled_products][][quantity]`;
        bundledProductsQuantityProperty.value = item.quantity;
        whereToAddItemsContainer.appendChild(bundledProductsQuantityProperty);

        const bundledProductsSkuProperty = document.createElement('input');
        bundledProductsSkuProperty.type = 'hidden';
        bundledProductsSkuProperty.name = `properties[_bundled_products][][sku]`;
        bundledProductsSkuProperty.value = item.sku || '';
        whereToAddItemsContainer.appendChild(bundledProductsSkuProperty);
      });

      const detailsToShow = document.createElement('input');
      detailsToShow.type = 'hidden';
      detailsToShow.name = 'properties[Modele]';
      detailsToShow.value = bundledProductsData
        .map(
          (variant) =>
            `${
              selectedVariants.find(
                (v) => v.id.toString() === variant.variant_id
              ).title
            } x ${variant.quantity}`
        )
        .join(', ');
      whereToAddItemsContainer.appendChild(detailsToShow);

      document.querySelectorAll("product-info-upsell").forEach((el, idx) => {
          if (el.dataset.selected === 'true') {
            const upsellInputId = document.createElement('input');
            upsellInputId.type = 'hidden';
            upsellInputId.name = `items[${idx+1}][id]`;
            upsellInputId.value = el.dataset.id;

            const upsellInputQty = document.createElement('input');
            upsellInputQty.type = 'hidden';
            upsellInputQty.name = `items[${idx+1}][quantity]`;
            upsellInputQty.value = '1';

            whereToAddItemsContainer.appendChild(upsellInputId);
            whereToAddItemsContainer.appendChild(upsellInputQty);
          }
        }
      );

      realSubmitButton.click();
    } catch (error) {
      console.error('Error adding bundle to cart:', error);
      BundleUtilities.showNotification(
        'Error adding bundle to cart. Please try again.',
        'error'
      );
    } finally {
      submitButton.classList.remove('loading');
      this.updateSubmitButton();
    }
  }

  /**
   * Reset all selections
   */
  resetSelections() {
    this.selections = {};

    document.querySelectorAll('.bundle-selection-item').forEach((item) => {
      const placeholder = item.querySelector('.bundle-selection-placeholder');
      const selected = item.querySelector('.bundle-selection-selected');

      placeholder.style.display = 'flex';
      selected.style.display = 'none';
      item.classList.remove('selected');
    });

    this.updateSubmitButton();
  }

  /**
   * Remove a selection
   */
  removeSelection(index) {
    delete this.selections[index];

    const item = document.querySelector(
      `.bundle-selection-item[data-selection-index="${index}"]`
    );
    if (item) {
      const placeholder = item.querySelector('.bundle-selection-placeholder');
      const selected = item.querySelector('.bundle-selection-selected');

      placeholder.style.display = 'flex';
      selected.style.display = 'none';
      item.classList.remove('selected');
    }

    this.updateSubmitButton();
    this.updateVariants(); // Refresh products to show removed item again
  }

  /**
   * Get current bundle state for debugging
   */
  getBundleState() {
    return {
      selections: this.selections,
      filteredProducts: this.filteredProducts,
      searchTerm: this.searchTerm,
      filters: this.filters,
      currentSelectionIndex: this.currentSelectionIndex,
    };
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Check if this is a bundle product page
  if (document.querySelector('.form--bundle')) {
    window.productBundleBuilder = new ProductBundleBuilder();
  }
});
